/**
 * Deterministic gradient avatars — generates a stable HSL gradient from a
 * string seed (customer name, user name, contact name), and a 2-letter
 * uppercase initial set. Mirrors the Domo customer-management app's
 * `crmAvatarHtml()` pattern so the visual identity is consistent.
 */

function hash(s: string | null | undefined): number {
  const str = s ?? ''; // real Domo data can carry a null name; mock fixtures never did
  let h = 0;
  for (let i = 0; i < str.length; i++)
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function initialsOf(name: string | null | undefined): string {
  if (!name) return '·';
  const parts = name
    .replace(/[^A-Za-z0-9 ,&-]/g, '')
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function avatarGradient(seed: string | null | undefined): string {
  const h = hash(seed);
  const h1 = h % 360;
  const h2 = (h1 + 35 + (h % 17)) % 360;
  const s = 58 + ((h >> 3) % 14); // 58-72%
  const l1 = 50 + ((h >> 5) % 8); // 50-58%
  const l2 = Math.max(28, l1 - 18);
  return `linear-gradient(135deg, hsl(${h1}, ${s}%, ${l1}%) 0%, hsl(${h2}, ${s}%, ${l2}%) 100%)`;
}

/**
 * Avatar-bg style object. Use as `<div style={avatarStyle(name, 32)}>`.
 */
export function avatarStyle(
  seed: string | null | undefined,
  size: number,
): React.CSSProperties {
  return {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    borderRadius: '50%',
    background: avatarGradient(seed),
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontSize: Math.round(size * 0.4),
    letterSpacing: '-0.005em',
    flexShrink: 0,
    boxShadow:
      'inset 0 0 0 1px rgba(255,255,255,0.18), 0 1px 2px rgba(26,18,23,0.18)',
    userSelect: 'none',
  };
}
